import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Starter } from '../../types/baking';

interface StarterFeedModalProps {
  starter: Starter;
  isOpen: boolean;
  onClose: () => void;
  onFeed: () => void;
}

export function StarterFeedModal({ starter, isOpen, onClose, onFeed }: StarterFeedModalProps) {
  if (!isOpen) return null;

  const hoursSinceLastFeed = 
    (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
  
  const isUrgent = starter.storage === 'room' 
    ? hoursSinceLastFeed >= 72 
    : hoursSinceLastFeed >= 240;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-md text-white">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <h2 className="text-2xl text-white font-brand font-light italic">Urgent Feed Required</h2>
            </div>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-red-500/40 backdrop-blur-sm rounded-lg border border-red-500/20">
            <p className="text-white">
              <span className="font-semibold">{starter.name}</span> is in urgent need of feeding! 
              {starter.storage === 'room' 
                ? ' Room temperature starters should be fed every 24 hours.'
                : ' Refrigerated starters should be fed every 7 days.'}
            </p>
          </div>

          <div className="text-sm text-white/80">
            <p>Last fed: {new Date(starter.lastFed).toLocaleString()}</p>
            <p className="mt-1">
              Time since last feed: {Math.floor(hoursSinceLastFeed)} hours
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
            >
              Remind me later
            </button>
            <button
              onClick={onFeed}
              className="px-4 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl transition-all duration-300"
            >
              Feed Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}